import { DateTime } from 'luxon';
import { compact, findLast, isNil, maxBy, minBy } from 'lodash-es';
import {
  type GqlCompany,
  type GqlShareTransactionGroup,
  type GqlSnrShareCapitalChange,
  GqlTransactionKind,
} from '../../../../graphql/base-types.graphql';

interface DateSortRange {
  minDate: DateTime;
  maxDate: DateTime;
  minSort: number;
  maxSort: number;
}

export const maybeDate = (date: string | null | undefined) =>
  date ? DateTime.fromISO(date).startOf('day') : undefined;

export function validDateAndSortOrderRange(
  stgs: Pick<GqlShareTransactionGroup, 'transactionDate' | 'sortOrder' | 'kind' | 'snrShareCapitalChangeId'>[],
  snrSccs: Pick<GqlSnrShareCapitalChange, 'id' | 'registrationDate'>[],
  snrScc:
    | { id: string; decisionDate?: string | undefined | null; registrationDate: string | undefined | null }
    | undefined,
  kind: GqlTransactionKind,
  company: Pick<GqlCompany, 'foundationDate'>,
): DateSortRange {
  if (isNil(snrScc) || snrSccs.length === 0) {
    return {
      minDate: DateTime.min(
        ...stgs.map(s => DateTime.fromISO(s.transactionDate)),
        DateTime.fromISO(company.foundationDate),
      ),
      maxDate: DateTime.local().plus({ years: 5 }).endOf('year').startOf('day'),
      minSort: (stgs[0]?.sortOrder ?? 0) + 1,
      maxSort: Infinity,
    };
  }

  if (kind === GqlTransactionKind.BlockPartition) {
    const maxDate = DateTime.local().plus({ years: 5 }).startOf('day');
    const latestStg = maxBy(stgs, stg => DateTime.fromISO(stg.transactionDate).toMillis() + stg.sortOrder);
    const minDate = latestStg ? DateTime.fromISO(latestStg.transactionDate) : DateTime.fromISO(company.foundationDate);
    const minSort = latestStg ? latestStg.sortOrder + 1 : 0;
    const maxSort = Infinity;
    return { maxDate, minDate, minSort, maxSort };
  } else if (kind === GqlTransactionKind.Transfer) {
    const maxDate = DateTime.local().plus({ years: 5 }).startOf('day');
    const latestStg = minBy(stgs, stg => DateTime.fromISO(stg.transactionDate).toMillis());
    const minDate = latestStg ? DateTime.fromISO(latestStg.transactionDate) : DateTime.fromISO(company.foundationDate);
    const minSort = 0;
    const maxSort = Infinity;
    return { maxDate, minDate, minSort, maxSort };
  }

  const snrSccIdx = snrSccs.findIndex(e => e.id === snrScc.id);
  const prevSnrScc = snrSccIdx > 0 ? snrSccs[snrSccIdx - 1] : snrSccs[0];
  const nextSnrScc = snrSccIdx !== snrSccs.length - 1 ? snrSccs[snrSccIdx + 1] : null;

  const prevStg = findLast(
    stgs,
    stg =>
      [
        GqlTransactionKind.Issue,
        GqlTransactionKind.Unissue,
        GqlTransactionKind.Split,
        GqlTransactionKind.Merge,
      ].includes(stg.kind) && stg.snrShareCapitalChangeId === prevSnrScc?.id,
  );
  let nextStg;
  if (!isNil(nextSnrScc)) {
    nextStg = stgs.find(stg => stg.snrShareCapitalChangeId === nextSnrScc.id);
  }

  const minDates: Record<string, DateTime | undefined> = {
    foundationDate: DateTime.fromISO(company.foundationDate).startOf('day'),
    previousTransactionDate: maybeDate(prevStg?.transactionDate),
    previousSnrCase: DateTime.fromISO(snrSccIdx > 0 ? snrScc.decisionDate! : company.foundationDate),
  };
  const maxDates: Record<string, DateTime | undefined> = {
    nextTransactionDate: maybeDate(nextStg?.transactionDate),
    thisSnrCaseRegistrationDate: maybeDate(snrScc?.registrationDate),
    nextSnrCaseRegistrationDate: maybeDate(nextSnrScc?.registrationDate),
    today: DateTime.local().startOf('day'),
    max: DateTime.local().plus({ year: 5 }).startOf('day'),
  };
  if (kind === GqlTransactionKind.Merge || kind === GqlTransactionKind.Split) {
    maxDates.thisSnrCaseRegistrationDate = undefined;
  }

  const minDate = DateTime.max(...compact(Object.values(minDates)))!;
  const maxDate = DateTime.min(...compact(Object.values(maxDates)))!;

  const ret = {
    minDate,
    maxDate,
    minSort: (prevStg?.sortOrder ?? 0) + 1,
    maxSort: nextStg?.sortOrder ?? Infinity,
  };
  return ret;
}
